import React, { useState, useEffect } from 'react';
import { Clock, MapPin, CreditCard, Package, CheckCircle, XCircle, AlertCircle, Eye, EyeOff, MessageCircle, QrCode, Copy, ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config/environment';

// Configuração do WhatsApp da empresa
const WHATSAPP_NUMERO = '5554992159272'; // Número da empresa: +55 48 88353555 (formato: 55 + DDD + número sem espaços/hífens)

interface Pedido {
  id: number;
  mesa: string;
  status: string;
  total: number;
  total_sem_frete: number;
  valor_frete: number;
  tipo_pedido: 'mesa' | 'entrega';
  metodo_pagamento: string;
  metodo_pagamento_detalhado: string;
  status_pagamento: 'pendente' | 'pago' | 'cancelado';
  created_at: string;
  observacoes: string;
  endereco_entrega: any;
  itens: ItemPedido[];
  total_itens: number;
  nome_completo_cliente: string;
  nome_cliente?: string;
  sobrenome_cliente?: string;
  telefone_cliente?: string;
  email_cliente?: string;
  detalhes_pagamento?: {
    qr_code?: string;
    qr_code_base64?: string;
    payment_id?: string;
    status?: string;
  };
}

interface ItemPedido {
  id: number;
  produto_id: number;
  produto_nome: string;
  imagem_url: string;
  quantidade: number;
  preco_unitario: number;
  observacoes: string;
  personalizacoes: Personalizacao[];
}

interface Personalizacao {
  id: number;
  nome: string;
  tipo: 'adicionar' | 'remover';
  quantidade: number;
  preco_adicional: number;
}

interface HistoricoPedidosProps {
  isOpen: boolean;
  onClose: () => void;
  podeVoltar?: boolean;
  onVoltar?: () => void;
  modalMaxHeightStyle?: React.CSSProperties;
  modalWidthStyle?: React.CSSProperties;
  deviceType?: 'mobile' | 'desktop' | 'tablet';
}

const HistoricoPedidos: React.FC<HistoricoPedidosProps> = ({ 
  isOpen, 
  onClose, 
  podeVoltar, 
  onVoltar,
  modalMaxHeightStyle,
  modalWidthStyle,
  deviceType = 'desktop'
}) => {
  const { usuario } = useAuth();
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pedidoParaCancelar, setPedidoParaCancelar] = useState<Pedido | null>(null);
  const [pedidoSelecionado, setPedidoSelecionado] = useState<Pedido | null>(null);

  useEffect(() => {
    if (isOpen && usuario?.id) {
      carregarPedidos();
    }
  }, [isOpen, usuario?.id]);

  const carregarPedidos = async () => {
    if (!usuario?.id) {
      console.error('Usuário não autenticado');
      return;
    }
    
    // Verificar se o token está presente antes de fazer a requisição
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('Token de autenticação não encontrado');
      toast.error('Você precisa estar logado para ver seus pedidos', { duration: 2000 });
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get(`/api/pedidos/usuario/${usuario.id}`);
      setPedidos(response.data);
    } catch (error: any) {
      console.error('Erro ao carregar pedidos:', error);
      
      // Tratar erros específicos sem depender do interceptor
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Sua sessão expirou. Por favor, faça login novamente.', { duration: 2000 });
        // Não redirecionar automaticamente - deixar o usuário decidir
      } else {
        toast.error('Erro ao carregar pedidos. Tente novamente.', { duration: 2000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pendente':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'preparando':
        return <Package className="h-5 w-5 text-blue-500" />;
      case 'pronto':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'entregue':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'cancelado':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusProgress = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pendente':
        return { progress: 25, steps: ['Pedido Recebido', 'Preparando', 'Pronto', 'Entregue'] };
      case 'preparando':
        return { progress: 50, steps: ['Pedido Recebido', 'Preparando', 'Pronto', 'Entregue'] };
      case 'pronto':
        return { progress: 75, steps: ['Pedido Recebido', 'Preparando', 'Pronto', 'Entregue'] };
      case 'entregue':
        return { progress: 100, steps: ['Pedido Recebido', 'Preparando', 'Pronto', 'Entregue'] };
      case 'cancelado':
        return { progress: 0, steps: ['Pedido Cancelado'] };
      default:
        return { progress: 0, steps: ['Status Desconhecido'] };
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pendente':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'preparando':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'pronto':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'entregue':
        return 'bg-green-600/20 text-green-500 border-green-600/30';
      case 'cancelado':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getPagamentoIcon = (status: string) => {
    switch (status) {
      case 'pago':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pendente':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'cancelado':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPagamentoColor = (status: string) => {
    switch (status) {
      case 'pago':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pendente':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'cancelado':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };


  const cancelarPedido = (pedido: Pedido) => {
    // Verificar se o pedido pode ser cancelado
    if (pedido.status_pagamento !== 'pendente') {
      toast.error('Este pedido não pode ser cancelado pois já foi pago.', { duration: 2000 });
      return;
    }

    // Mostrar modal de confirmação personalizado
    setPedidoParaCancelar(pedido);
    setShowCancelModal(true);
  };

  const confirmarCancelamento = () => {
    if (!pedidoParaCancelar) return;

    // Usar o número configurado
    const numeroWhatsApp = WHATSAPP_NUMERO;
    
    // Validar se o número está no formato correto
    if (!numeroWhatsApp || numeroWhatsApp.length < 10) {
      toast.error('Número do WhatsApp não configurado corretamente.', { duration: 2000 });
      return;
    }
    
    // Montar mensagem de cancelamento
    const mensagem = `Olá! Gostaria de cancelar meu pedido:

📋 *Pedido #${pedidoParaCancelar.id}*
👤 Cliente: ${pedidoParaCancelar.nome_completo_cliente || `${pedidoParaCancelar.nome_cliente || ''} ${pedidoParaCancelar.sobrenome_cliente || ''}`.trim()}
📱 Telefone: ${pedidoParaCancelar.telefone_cliente || 'Não informado'}
📧 Email: ${pedidoParaCancelar.email_cliente || 'Não informado'}
💰 Valor: R$ ${pedidoParaCancelar.total.toFixed(2)}
📅 Data: ${formatarData(pedidoParaCancelar.created_at)}

Por favor, confirme o cancelamento deste pedido.

Obrigado!`;

    // Codificar a mensagem para URL
    const mensagemCodificada = encodeURIComponent(mensagem);
    
    // Criar URL do WhatsApp
    const urlWhatsApp = `https://wa.me/${numeroWhatsApp}?text=${mensagemCodificada}`;
    
    // Log para debug
    console.log('URL do WhatsApp:', urlWhatsApp);
    console.log('Número usado:', numeroWhatsApp);
    
    // Abrir WhatsApp
    const novaJanela = window.open(urlWhatsApp, '_blank');
    
    // Verificar se a janela foi aberta
    if (!novaJanela) {
      toast.error('Não foi possível abrir o WhatsApp. Verifique se o navegador permite pop-ups.', { duration: 2000 });
      return;
    }
    
    // Fechar modal e limpar estado
    setShowCancelModal(false);
    setPedidoParaCancelar(null);
    
    // Mostrar toast de sucesso
    toast.success('Redirecionando para o WhatsApp...', { duration: 1500 });
  };

  const cancelarModal = () => {
    setShowCancelModal(false);
    setPedidoParaCancelar(null);
  };

  // Verificar se o modal está aberto e se o usuário está autenticado
  if (!isOpen) return null;
  
  // Se não houver usuário, mostrar mensagem ao invés de redirecionar
  if (!usuario || !usuario.id) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-fade-in">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-md border border-gray-600/50 shadow-2xl">
          <div className="p-6">
            <div className="text-center">
              <h2 className="text-xl font-bold text-white mb-4">Acesso Restrito</h2>
              <p className="text-gray-300 mb-6">Você precisa estar logado para ver seus pedidos.</p>
              <button
                onClick={onClose}
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 px-6 rounded-xl font-bold hover:from-orange-600 hover:to-red-600 transition-all duration-300"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4 z-[1100] animate-fade-in">
      <div 
        className="bg-gradient-to-br from-gray-800 to-gray-900 w-full flex flex-col shadow-2xl border-x border-b border-gray-600/50 sm:border sm:rounded-2xl overflow-hidden transition-all duration-300"
        style={{ ...modalMaxHeightStyle, ...modalWidthStyle }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-3 sm:p-5 border-b border-gray-700/50 flex justify-between items-center flex-shrink-0">
            <div className="flex items-center gap-2">
              {podeVoltar && (
                <button
                  onClick={onVoltar}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Voltar"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              )}
              <div>
                <h2 className="text-base sm:text-xl font-bold text-white leading-tight">Meus Pedidos</h2>
                <p className="text-[10px] sm:text-xs text-gray-400">Acompanhe e visualize seus pedidos</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Sair"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>

          {/* Lista de Pedidos com scroll robusto */}
          <div className="overflow-y-auto flex-1 p-4 sm:p-6 custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>

          {/* Lista de Pedidos */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
            </div>
          ) : pedidos.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">📦</div>
              <p className="text-gray-400 text-lg">
                Você ainda não fez nenhum pedido
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Faça seu primeiro pedido e ele aparecerá aqui!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {pedidos.map((pedido) => (
                <div key={pedido.id} className="bg-gradient-to-br from-gray-700/60 to-gray-800/60 rounded-2xl p-4 sm:p-6 border border-gray-600/40 shadow-lg backdrop-blur-sm">
                  {/* Header do Pedido */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(pedido.status)}
                        <span className="font-bold text-white text-lg">
                          Pedido #{pedido.id}
                        </span>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(pedido.status)}`}>
                        {pedido.status.charAt(0).toUpperCase() + pedido.status.slice(1)}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-400 text-sm">
                        {formatarData(pedido.created_at)}
                      </p>
                      <p className="font-bold text-lg bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                        R$ {pedido.total.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Informações do Pedido */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-blue-400" />
                      <div>
                        <p className="text-xs text-gray-400">Tipo</p>
                        <p className="text-sm font-medium text-white">
                          {pedido.tipo_pedido === 'entrega' ? 'Entrega' : 'Mesa'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-purple-400" />
                      <div>
                        <p className="text-xs text-gray-400">Pagamento</p>
                        <div className="flex items-center gap-1">
                          {getPagamentoIcon(pedido.status_pagamento)}
                          <span className={`text-xs px-2 py-1 rounded-full border ${getPagamentoColor(pedido.status_pagamento)}`}>
                            {pedido.status_pagamento.charAt(0).toUpperCase() + pedido.status_pagamento.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-orange-400" />
                      <div>
                        <p className="text-xs text-gray-400">Itens</p>
                        <p className="text-sm font-medium text-white">
                          {pedido.total_itens} {pedido.total_itens === 1 ? 'item' : 'itens'}
                        </p>
                      </div>
                    </div>

                    {pedido.tipo_pedido === 'entrega' && pedido.valor_frete > 0 && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-green-400" />
                        <div>
                          <p className="text-xs text-gray-400">Frete</p>
                          <p className="text-sm font-medium text-white">
                            R$ {pedido.valor_frete.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Botões de ação */}
                  <div className="flex gap-3">
                    {/* Botão para ver detalhes */}
                    <button
                      onClick={() => setPedidoSelecionado(pedido)}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 rounded-xl font-bold hover:brightness-110 transition-all flex items-center justify-center gap-2 shadow-lg active:scale-95"
                    >
                      <Eye className="h-4 w-4" />
                      Ver Detalhes
                    </button>

                    {/* Botão de cancelar (só aparece se pagamento estiver pendente) */}
                    {pedido.status_pagamento === 'pendente' && (
                      <button
                        onClick={() => cancelarPedido(pedido)}
                        className="bg-gray-700 text-white py-3 px-6 rounded-xl font-bold hover:bg-gray-600 transition-all border border-gray-600 shadow-md active:scale-95 flex items-center justify-center gap-2"
                        title="Cancelar pedido via WhatsApp"
                      >
                        <MessageCircle className="h-4 w-4" />
                        Cancelar
                      </button>
                    )}
                  </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>

      {/* Modal de Detalhes do Pedido Selecionado */}
      {pedidoSelecionado && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md flex items-center justify-center p-0 sm:p-4 z-[70] animate-fade-in">
          <div 
            className="bg-gradient-to-br from-gray-800 to-gray-900 w-full flex flex-col shadow-2xl sm:rounded-2xl overflow-hidden"
            style={{ ...modalMaxHeightStyle, ...modalWidthStyle }}
          >
            {/* Header do Detalhe */}
            <div className="p-3 sm:p-5 border-b border-gray-700/50 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPedidoSelecionado(null)}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Voltar"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h3 className="text-base sm:text-xl font-bold text-white">Pedido #{pedidoSelecionado?.id}</h3>
                  <p className="text-[10px] sm:text-xs text-gray-400">{formatarData(pedidoSelecionado?.created_at || '')}</p>
                </div>
              </div>
              <button
                onClick={() => setPedidoSelecionado(null)}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Fechar"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            {/* Conteúdo do Detalhe com Scroll */}
            <div className="overflow-y-auto flex-1 p-4 sm:p-6 custom-scrollbar pb-10" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="space-y-6">
                {/* Status e Resumo */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gray-700/30 p-4 rounded-2xl border border-gray-600/50">
                  <div className="flex items-center gap-3">
                    {pedidoSelecionado && getStatusIcon(pedidoSelecionado.status)}
                    <div>
                      <p className="text-xs text-gray-400 uppercase font-bold tracking-wider">Status do Pedido</p>
                      <p className={`text-sm font-bold ${pedidoSelecionado ? getStatusColor(pedidoSelecionado.status) : ''}`}>
                        {pedidoSelecionado?.status ? (pedidoSelecionado.status.charAt(0).toUpperCase() + pedidoSelecionado.status.slice(1)) : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-xs text-gray-400 uppercase font-bold tracking-wider">Total do Pedido</p>
                    <p className="text-2xl font-black bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
                      R$ {pedidoSelecionado?.total.toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Timeline de Acompanhamento */}
                <div className="bg-gray-700/20 rounded-2xl p-5 border border-gray-700/50">
                  <h4 className="font-bold text-white mb-4 flex items-center gap-2 text-sm uppercase tracking-widest text-gray-400">
                    <Clock className="h-4 w-4" />
                    Acompanhamento
                  </h4>
                  {pedidoSelecionado && (() => {
                    const { progress, steps } = getStatusProgress(pedidoSelecionado.status);
                    return (
                      <div className="space-y-6">
                        <div className="relative">
                          <div className="h-2 bg-gray-600 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-orange-500 to-red-600 transition-all duration-1000"
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                          <div className="flex justify-between mt-4">
                            {steps.map((step, index) => {
                              const isCompleted = index * 25 < progress;
                              const isCurrent = Math.floor(progress / 25) === index;
                              return (
                                <div key={index} className="flex flex-col items-center flex-1">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 mb-2 transition-all ${
                                    isCompleted ? 'bg-green-500 border-green-500 text-white' : 
                                    isCurrent ? 'bg-orange-500 border-orange-500 text-white animate-pulse' : 
                                    'bg-gray-800 border-gray-700 text-gray-500'
                                  }`}>
                                    {isCompleted ? <CheckCircle className="h-4 w-4" /> : <span className="text-[10px] font-bold">{index + 1}</span>}
                                  </div>
                                  <span className={`text-[9px] font-bold text-center leading-tight ${isCompleted || isCurrent ? 'text-white' : 'text-gray-500'}`}>
                                    {step}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Itens do Pedido */}
                <div className="space-y-4">
                  <h4 className="font-bold text-white flex items-center gap-2 text-sm uppercase tracking-widest text-gray-400">
                    <Package className="h-4 w-4" />
                    Itens do Pedido ({pedidoSelecionado?.total_itens})
                  </h4>
                  <div className="space-y-3">
                    {pedidoSelecionado?.itens.map((item) => (
                      <div key={item.id} className="bg-gray-800/40 p-3 rounded-2xl border border-gray-700/30 flex gap-4 transition-all hover:bg-gray-800/60">
                        {item.imagem_url && (
                          <img
                            src={item.imagem_url.startsWith('http') ? item.imagem_url : `${config.api.baseUrl}/${item.imagem_url}`}
                            alt={item.produto_nome}
                            className="w-16 h-16 rounded-xl object-cover shadow-md"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <h5 className="font-bold text-white text-sm sm:text-base leading-tight">{item.produto_nome}</h5>
                            <span className="font-black text-orange-400 text-sm ml-2">
                              R$ {(item.preco_unitario * item.quantidade).toFixed(2)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Quantidade: {item.quantidade}x</p>
                          {item.personalizacoes && item.personalizacoes.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {item.personalizacoes.map((p, idx) => (
                                <p key={idx} className="text-[10px] text-gray-400 flex items-center gap-1">
                                  <span className={p.tipo === 'adicionar' ? 'text-green-500' : 'text-red-500'}>
                                    {p.tipo === 'adicionar' ? '+' : '-'}
                                  </span>
                                  {p.nome}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Endereço e Pagamento */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {pedidoSelecionado?.tipo_pedido === 'entrega' && (
                    <div className="bg-gray-800/40 p-4 rounded-2xl border border-gray-700/30">
                      <h4 className="font-bold text-white mb-3 flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400">
                        <MapPin className="h-4 w-4 text-blue-400" />
                        Endereço de Entrega
                      </h4>
                      <p className="text-xs text-white leading-relaxed">
                        {pedidoSelecionado.endereco_entrega?.rua}, {pedidoSelecionado.endereco_entrega?.numero_casa}<br/>
                        {pedidoSelecionado.endereco_entrega?.bairro} - {pedidoSelecionado.endereco_entrega?.cep}<br/>
                        {pedidoSelecionado.endereco_entrega?.cidade}
                      </p>
                    </div>
                  )}
                  <div className="bg-gray-800/40 p-4 rounded-2xl border border-gray-700/30">
                    <h4 className="font-bold text-white mb-3 flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400">
                      <CreditCard className="h-4 w-4 text-purple-400" />
                      Pagamento
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2 py-1 rounded-full border font-bold uppercase tracking-tighter ${pedidoSelecionado ? getPagamentoColor(pedidoSelecionado.status_pagamento) : ''}`}>
                        {pedidoSelecionado?.status_pagamento}
                      </span>
                      <p className="text-xs text-gray-300">via {pedidoSelecionado?.metodo_pagamento_detalhado}</p>
                    </div>
                  </div>
                </div>

                {/* PIX Payment Section inside Details if pending */}
                {pedidoSelecionado?.status_pagamento === 'pendente' && pedidoSelecionado.detalhes_pagamento?.qr_code && (
                   <div className="p-5 bg-green-500/5 rounded-2xl border border-green-500/20 text-center">
                      <h4 className="text-green-400 font-bold mb-4 flex items-center justify-center gap-2">
                        <QrCode className="h-5 w-5" />
                        Pagamento via PIX
                      </h4>
                      {pedidoSelecionado.detalhes_pagamento.qr_code_base64 && (
                        <div className="bg-white p-4 rounded-2xl inline-block shadow-2xl mb-4">
                          <img 
                            src={`data:image/png;base64,${pedidoSelecionado.detalhes_pagamento.qr_code_base64}`} 
                            alt="QR Code PIX" 
                            className="w-48 h-48"
                          />
                        </div>
                      )}
                      <button
                        onClick={async () => {
                          if (pedidoSelecionado?.detalhes_pagamento?.qr_code) {
                            await navigator.clipboard.writeText(pedidoSelecionado.detalhes_pagamento.qr_code);
                            toast.success('Pilha PIX copiada!', { duration: 1500 });
                          }
                        }}
                        className="w-full bg-green-600 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 transition-all shadow-lg shadow-green-600/20"
                      >
                        <Copy className="h-4 w-4" />
                        Copiar Código PIX
                      </button>
                   </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Cancelamento */}
      {showCancelModal && pedidoParaCancelar && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-[60] animate-fade-in">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-md border border-gray-600/50 shadow-2xl">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">
                    Cancelar Pedido
                  </h3>
                  <p className="text-sm text-gray-400">
                    Confirmação necessária
                  </p>
                </div>
              </div>

              {/* Conteúdo */}
              <div className="mb-6">
                <p className="text-white mb-4">
                  Tem certeza que deseja cancelar o <span className="font-bold text-red-400">Pedido #{pedidoParaCancelar?.id}</span>?
                </p>
                
                <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Valor:</span>
                      <span className="text-white font-semibold">R$ {pedidoParaCancelar?.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Data:</span>
                      <span className="text-white">{pedidoParaCancelar && formatarData(pedidoParaCancelar.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Status:</span>
                      <span className="text-yellow-400 font-semibold">Pendente</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-sm text-blue-300">
                    <MessageCircle className="h-4 w-4 inline mr-2" />
                    Você será redirecionado para o WhatsApp para confirmar o cancelamento.
                  </p>
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3">
                <button
                  onClick={cancelarModal}
                  className="flex-1 bg-gray-600 text-white py-3 rounded-xl font-bold hover:bg-gray-700 transition-all duration-300"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarCancelamento}
                  className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 rounded-xl font-bold hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <MessageCircle className="h-4 w-4" />
                  Ir para WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricoPedidos;
